import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, MONTHS_FR, TECH_FUNCTIONS, OUVREUSE_FUNCTIONS, SPECIFICATIONS, VACATIONS, COMPLEMENTS, sortCrewMembers } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Save, Plus, Trash2, HardHat, Ticket, Wand2, Repeat, FileDown, Send, Printer } from "lucide-react";
import ReplacementModal from "@/components/ReplacementModal";

const TABS = [
  { id: "recap", label: "Récap 48h", scope: "all",
    cols: ["num", "nom_prenom", "fonction", "specifications", "vacation", "heure_debut", "nbre_h", "cachet_brut", "complement", "total_brut", "def_hr", "due", "contrat", "vm", "date_debut", "date_fin", "horaires", "commentaires"] },
  { id: "onsite", label: "ON SITE", scope: "all",
    cols: ["present", "nom_prenom", "fonction", "specifications", "vacation", "heure_debut", "nbre_h", "cachet_brut", "total_brut", "contrat"] },
  { id: "presence", label: "Présence", scope: "all",
    cols: ["num", "nom_prenom", "fonction", "specifications", "vacation", "heure_debut", "nbre_h", "signature_matin", "signature_soir"] },
  { id: "codif_techs", label: "Codif Techs", scope: "tech",
    cols: ["nom_prenom", "fonction", "specifications", "vacation", "nbre_h", "cachet_brut", "complement", "total_brut", "def_hr", "commentaires"] },
  { id: "codif_ouvreuses", label: "Codif Ouvreuses", scope: "ouvreur",
    cols: ["num", "nom_prenom", "fonction", "specifications", "vacation", "nbre_h", "cachet_brut", "complement", "total_brut", "def_hr", "commentaires"] },
];

const LABELS = {
  num: "#", present: "Présent", nom_prenom: "NOM Prénom", fonction: "Fonction",
  specifications: "Spécifications", total_corps: "# Total par Corps de Métier", vacation: "Vacation", heure_debut: "Get in",
  nbre_h: "Nbre d'H", cachet_brut: "Cachet Brut", complement: "Complément",
  total_brut: "Total Brut", def_hr: "DEF H/R", due: "DPAE", contrat: "Contrat",
  vm: "VM", date_debut: "Date Début", date_fin: "Date Fin", horaires: "Horaires",
  commentaires: "Commentaires", signature_matin: "Signature Matin", signature_soir: "Signature Soir",
};

const MONEY = new Set(["cachet_brut", "total_brut"]);

const WIDE = new Set(["nom_prenom", "fonction", "specifications", "commentaires", "horaires", "vacation"]);

export default function SynthesePage({ embedded = false, besoins: besoinsProp, onDirtyChange } = {}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [concert, setConcert] = useState(null);
  const [crew, setCrew] = useState([]);
  const [tab, setTab] = useState("recap");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [besoinsState, setBesoinsState] = useState([]);
  // En mode intégré, on utilise les besoins EN DIRECT fournis par la fiche concert (prop),
  // sinon on retombe sur ceux chargés localement (page autonome).
  const besoins = besoinsProp !== undefined ? besoinsProp : besoinsState;
  const [repOpen, setRepOpen] = useState(false);
  const [busyPdf, setBusyPdf] = useState(false);

  // Ouvreuses présentes seulement si des besoins ouvreuses sont déclarés (ou déjà saisis dans l'équipe).
  // Lorsqu'il est intégré à la fiche concert, l'état des besoins ouvreuses est fourni en direct via la prop.
  const hasOuvreuses = useMemo(
    () => (besoins || []).some((b) => b.categorie === "Ouvreuses") || crew.some((m) => m.category === "ouvreur"),
    [besoins, crew]
  );

  useEffect(() => {
    if (!hasOuvreuses && tab === "codif_ouvreuses") setTab("recap");
  }, [hasOuvreuses, tab]);

  // Suivi des modifications non enregistrées de la synthèse.
  useEffect(() => { if (embedded) onDirtyChange?.(dirty); }, [dirty, embedded, onDirtyChange]);
  useEffect(() => {
    if (embedded) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    if (dirty) window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty, embedded]);

  const load = useCallback(async () => {
    try {
      const [{ data: c }, { data: cr }] = await Promise.all([
        api.get(`/concerts/${id}`),
        api.get(`/concerts/${id}/crew`),
      ]);
      setConcert(c);
      setBesoinsState(c.besoins || []);
      setCrew(cr.crew || []);
      setDirty(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const update = (mid, key, val) => {
    setDirty(true);
    setCrew((prev) => prev.map((m) => (m.id === mid ? { ...m, [key]: val } : m)));
  };

  const addMember = (category) => {
    const nm = {
      id: crypto.randomUUID(), category,
      nom_prenom: "", fonction: category === "ouvreur" ? "Ouvreur/se" : category === "catering" ? "Aide Catering" : "Technicien(ne) de Plateau",
      specifications: "", total_corps: "", vacation: category === "catering" ? "Matin" : "1ère vacation", heure_debut: "", nbre_h: "",
      cachet_brut: "", complement: "", total_brut: "", def_hr: "", due: "", contrat: "",
      vm: "", date_debut: "", date_fin: "", horaires: "", commentaires: "",
      present: false, signature_matin: "", signature_soir: "",
    };
    setDirty(true);
    setCrew((prev) => [...prev, nm]);
  };

  const removeMember = (mid) => { setDirty(true); setCrew((prev) => prev.filter((m) => m.id !== mid)); };

  const toNum = (v) => parseFloat(String(v ?? "").replace(",", ".")) || 0;

  const buildRowsFromBesoins = useCallback(() => {
    const order = { Techs: 0, Ouvreuses: 1, "Aides Catering": 2 };
    const rows = [];
    (besoins || []).slice().sort((a, b) => (order[a.categorie] ?? 0) - (order[b.categorie] ?? 0)).forEach((b) => {
      if (!b.fonction) return; // ligne générée seulement une fois le poste choisi
      const count = Math.max(1, parseInt(b.nombre || "1", 10) || 1);
      const isOuv = b.categorie === "Ouvreuses";
      const isCat = b.categorie === "Aides Catering";
      const total = isOuv ? toNum(b.cachet_brut) * toNum(b.nombre_heure) : toNum(b.cachet_brut);
      for (let i = 0; i < count; i++) {
        rows.push({
          id: crypto.randomUUID(), category: isCat ? "catering" : isOuv ? "ouvreur" : "tech", auto: true,
          nom_prenom: "", fonction: b.fonction || "", specifications: b.specifications || "", vacation: b.vacation || "",
          heure_debut: b.heure_debut || "", nbre_h: b.nombre_heure || "", cachet_brut: b.cachet_brut || "",
          complement: b.complement || "", total_brut: total ? total.toFixed(2) : "", def_hr: "", due: "", contrat: "", vm: "",
          date_debut: "", date_fin: "", horaires: "", commentaires: "", present: false,
          signature_matin: "", signature_soir: "",
        });
      }
    });
    return rows;
  }, [besoins]);

  const rowKey = (r) => `${r.category}|${r.fonction}|${r.specifications}|${r.vacation}|${r.nbre_h}`;

  // Synchronisation auto des lignes de synthèse avec les besoins (ajout ET suppression),
  // en préservant les lignes ajoutées/éditées manuellement (mode intégré).
  useEffect(() => {
    if (!embedded) return;
    const expected = buildRowsFromBesoins();
    setCrew((prev) => {
      const need = {}; expected.forEach((r) => { const k = rowKey(r); need[k] = (need[k] || 0) + 1; });
      const used = {};
      const kept = [];
      prev.forEach((r) => {
        if (!r.auto) { kept.push(r); return; }
        const k = rowKey(r);
        if ((used[k] || 0) < (need[k] || 0)) { used[k] = (used[k] || 0) + 1; kept.push(r); }
        // sinon: besoin supprimé -> on retire cette ligne auto
      });
      const toAdd = [];
      Object.entries(need).forEach(([k, n]) => {
        for (let i = used[k] || 0; i < n; i++) {
          toAdd.push({ ...expected.find((e) => rowKey(e) === k), id: crypto.randomUUID(), auto: true });
        }
      });
      const next = [...kept, ...toAdd];
      const idsA = prev.map((r) => r.id).sort().join(",");
      const idsB = next.map((r) => r.id).sort().join(",");
      if (idsA === idsB) return prev;
      setTimeout(() => setDirty(true), 0);
      return next;
    });
  }, [besoins, embedded, buildRowsFromBesoins]);

  const prefillFromBesoins = () => {
    const rows = buildRowsFromBesoins();
    if (!rows.length) { toast.error("Aucun besoin saisi sur ce concert"); return; }
    if (crew.length > 0 && !window.confirm("Remplacer les lignes actuelles par celles générées depuis les besoins ?")) return;
    setCrew(rows);
    setDirty(true);
    toast.success(`${rows.length} ligne(s) générée(s) depuis les besoins`);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/concerts/${id}/crew`, { crew, ouvreurs_enabled: hasOuvreuses });
      toast.success("Synthèse enregistrée");
      setDirty(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const download = (blob, name) => {
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u; a.download = name; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(u);
  };

  const doCodifsPdf = async () => {
    setBusyPdf(true);
    try {
      const r = await api.post(`/concerts/${id}/codifs-pdf`, { crew, ouvreurs_enabled: hasOuvreuses }, { responseType: "blob" });
      download(r.data, `Codifs-${(concert.concert || "concert").replace(/\s+/g, "_")}.pdf`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusyPdf(false); }
  };

  const doPresencePdf = async () => {
    setBusyPdf(true);
    try {
      const r = await api.post(`/concerts/${id}/presence-pdf`, { crew, ouvreurs_enabled: hasOuvreuses }, { responseType: "blob" });
      download(r.data, `Presence-${(concert.concert || "concert").replace(/\s+/g, "_")}.pdf`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusyPdf(false); }
  };

  const doSendCodifs = async () => {
    setBusyPdf(true);
    try {
      await api.post(`/concerts/${id}/send-codifs`, { crew, ouvreurs_enabled: hasOuvreuses });
      toast.success("Codifs envoyées à Bernard");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusyPdf(false); }
  };

  if (!concert) return <div className="text-muted-foreground">Chargement...</div>;

  const dateLabel = concert.day
    ? `${concert.day} ${MONTHS_FR[concert.month]} ${concert.year}`
    : `${MONTHS_FR[concert.month]} ${concert.year}`;

  const visibleTabs = TABS.filter((t) => t.id !== "codif_ouvreuses" || hasOuvreuses);
  const techCount = crew.filter((m) => m.category === "tech").length;
  const ouvreurCount = crew.filter((m) => m.category === "ouvreur").length;
  const cateringCount = crew.filter((m) => m.category === "catering").length;

  const renderTable = (t) => {
    const members = t.scope === "all"
      ? sortCrewMembers(crew)
      : sortCrewMembers(crew.filter((m) => m.category === t.scope));
    const firstTech = members.findIndex((m) => m.category === "tech");
    const firstOuv = members.findIndex((m) => m.category === "ouvreur");
    const firstCatering = members.findIndex((m) => m.category === "catering");
    const showGroups = t.scope === "all";
    const nCols = t.cols.length + 1;

    const cellInput = (m, col) => {
      const listId = col === "fonction" ? (m.category === "ouvreur" ? "fn-ouvreur" : "fn-tech")
        : col === "specifications" ? "spec-list" : col === "vacation" ? "vac-list" : col === "complement" ? "comp-list" : undefined;
      const input = (
        <Input value={m[col] ?? ""} onChange={(e) => update(m.id, col, e.target.value)} list={listId}
          className={`h-8 text-xs px-2 ${MONEY.has(col) ? "pr-5" : ""} ${WIDE.has(col) ? "min-w-[150px]" : "min-w-[80px]"}`}
          data-testid={`syn-${m.id}-${col}`} />
      );
      if (MONEY.has(col)) {
        return (
          <div className="relative">
            {input}
            <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">€</span>
          </div>
        );
      }
      return input;
    };

    return (
      <div className="rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="text-sm border-collapse min-w-full" data-testid={`synthese-table-${t.id}`}>
            <thead>
              <tr className="bg-secondary/60 text-muted-foreground">
                {t.cols.map((col) => (
                  <th key={col} className="px-2 py-2 text-left font-semibold whitespace-nowrap text-xs">{LABELS[col]}</th>
                ))}
                <th className="px-2 py-2 w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {members.length === 0 && (
                <tr><td colSpan={nCols} className="px-3 py-10 text-center text-muted-foreground">Aucune ligne</td></tr>
              )}
              {members.map((m, idx) => (
                <React.Fragment key={m.id}>
                  {showGroups && idx === firstTech && (
                    <tr><td colSpan={nCols} className="px-3 py-1.5 bg-amber-500/20 text-amber-200 font-semibold text-xs uppercase tracking-wider">Techniciens</td></tr>
                  )}
                  {showGroups && idx === firstOuv && (
                    <tr><td colSpan={nCols} className="px-3 py-1.5 bg-sky-500/20 text-sky-200 font-semibold text-xs uppercase tracking-wider">Ouvreuses</td></tr>
                  )}
                  {showGroups && idx === firstCatering && (
                    <tr><td colSpan={nCols} className="px-3 py-1.5 bg-emerald-500/20 text-emerald-200 font-semibold text-xs uppercase tracking-wider">Aides Catering</td></tr>
                  )}
                  <tr className={`hover:bg-secondary/40 ${m.replaced ? "opacity-50 line-through" : m.category === "ouvreur" ? "bg-sky-500/[0.06]" : m.category === "catering" ? "bg-emerald-500/[0.06]" : "bg-amber-500/[0.06]"}`} data-testid={`synthese-row-${m.id}`}>
                    {t.cols.map((col) => (
                      <td key={col} className="px-1.5 py-1">
                        {col === "num" ? (
                          <span className="text-muted-foreground text-xs px-1">{idx + 1}</span>
                        ) : col === "present" ? (
                          <input type="checkbox" checked={!!m.present}
                            onChange={(e) => update(m.id, "present", e.target.checked)}
                            className="h-4 w-4 accent-amber-500" data-testid={`syn-${m.id}-present`} />
                        ) : cellInput(m, col)}
                      </td>
                    ))}
                    <td className="px-1.5 py-1 text-center">
                      <button onClick={() => removeMember(m.id)} className="text-muted-foreground hover:text-destructive"
                        data-testid={`syn-remove-${m.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <datalist id="fn-tech">{TECH_FUNCTIONS.map((f) => <option key={f} value={f} />)}</datalist>
      <datalist id="fn-ouvreur">{OUVREUSE_FUNCTIONS.map((f) => <option key={f} value={f} />)}</datalist>
      <datalist id="spec-list">{SPECIFICATIONS.map((f) => <option key={f} value={f} />)}</datalist>
      <datalist id="vac-list">{VACATIONS.map((f) => <option key={f} value={f} />)}</datalist>
      <datalist id="comp-list">{COMPLEMENTS.map((f) => <option key={f} value={f} />)}</datalist>
      {!embedded && (
        <button onClick={() => { if (dirty && !window.confirm("Des modifications non enregistrées seront perdues. Quitter la page ?")) return; navigate(`/concerts/${id}`); }} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-concert">
          <ArrowLeft className="h-4 w-4" /> Retour à la date
        </button>
      )}

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          {embedded ? (
            <>
              <h2 className="font-display text-lg font-bold">Synthèse</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {techCount} technicien(s){hasOuvreuses ? ` · ${ouvreurCount} ouvreur(s)` : ""}{cateringCount ? ` · ${cateringCount} aide(s) catering` : ""}
              </p>
            </>
          ) : (
            <>
              <div className="text-xs uppercase tracking-[0.2em] text-primary">{dateLabel}</div>
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">
                Synthèse — {concert.concert}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {techCount} technicien(s){hasOuvreuses ? ` · ${ouvreurCount} ouvreur(s)` : ""}{cateringCount ? ` · ${cateringCount} aide(s) catering` : ""}
              </p>
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm" onClick={prefillFromBesoins} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="prefill-from-besoins-button">
            <Wand2 className="h-4 w-4 mr-1.5" /> Pré-remplir depuis les besoins
          </Button>
          <Button onClick={save} disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="save-synthese-button">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => setRepOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="declare-replacement-button">
          <Repeat className="h-4 w-4 mr-1.5" /> Déclarer un remplacement
        </Button>
        <Button size="sm" onClick={doCodifsPdf} disabled={busyPdf} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="download-codifs-button">
          <FileDown className="h-4 w-4 mr-1.5" /> Télécharger les codifs (PDF)
        </Button>
        <Button size="sm" onClick={doSendCodifs} disabled={busyPdf} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="send-codifs-button">
          <Send className="h-4 w-4 mr-1.5" /> Envoyer les codifs à Bernard
        </Button>
        <Button size="sm" onClick={doPresencePdf} disabled={busyPdf} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="print-presence-button">
          <Printer className="h-4 w-4 mr-1.5" /> Imprimer la feuille de présence
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" onClick={() => addMember("tech")} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="add-tech-button">
          <Plus className="h-4 w-4 mr-1.5" /> Technicien
        </Button>
        <Button size="sm" onClick={() => addMember("ouvreur")} disabled={!hasOuvreuses} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="add-ouvreur-button">
          <Plus className="h-4 w-4 mr-1.5" /> Ouvreur/se
        </Button>
        <Button size="sm" onClick={() => addMember("catering")} className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="add-catering-button">
          <Plus className="h-4 w-4 mr-1.5" /> Aide Catering
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto">
          {visibleTabs.map((t) => (
            <TabsTrigger key={t.id} value={t.id} data-testid={`synthese-tab-${t.id}`}>
              {t.id === "codif_techs" && <HardHat className="h-3.5 w-3.5 mr-1.5" />}
              {t.id === "codif_ouvreuses" && <Ticket className="h-3.5 w-3.5 mr-1.5" />}
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {visibleTabs.map((t) => (
          <TabsContent key={t.id} value={t.id} className="mt-4">
            {renderTable(t)}
          </TabsContent>
        ))}
      </Tabs>

      <ReplacementModal open={repOpen} onOpenChange={setRepOpen} crew={crew} setCrew={(u) => { setDirty(true); setCrew(u); }} />
    </div>
  );
}
